import React, { FC, useState } from "react";

// UI components
import { Card } from "@UI/Components/Card/Card";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { InfoTooltip } from "@UI/Components/InfoTooltip/InfoTooltip";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { Drawer } from "@UI/Components/Drawer/Drawer";
import { EuroCircle } from "@UI/Components/Icons/EuroCircle";

// Freemium
import { useServiceFeesFeature } from "@modules/ServiceFees/hooks/useServiceFeesFeature";

import { ResponsiveFormattedMessage } from "@Shared/Components/ResponsiveFormattedMessage/ResponsiveFormattedMessage";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

// hooks
import useMediaQuery from "@UI/hooks/useMediaQuery";
import useI18n from "@I18n/hooks/useI18n";

// services
import { IServiceOffer } from "@modules/ServiceAssignment/services/serviceOffers";
import { IServiceProvider } from "@modules/ServiceProviders/services/serviceProviders";

// routes
import {
  RouterLink,
  RouterLinkProps,
} from "@Libs/Router/Components/RouterLink/RouterLink";
import { ServiceOfferPreview } from "@modules/ServiceAssignment/Components/ServiceOfferPreview/ServiceOfferPreview";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import { ChooseFormulaPageRedesignServiceFeeFormulaCard } from "./ABTests/ChooseFormulaPageRedesign/ChooseFormulaPageRedesignServiceFeeFormulaCard";

interface Props {
  serviceProvider: IServiceProvider;
  serviceOffer: IServiceOffer;
  link: RouterLinkProps;
}

export const ServiceFeeFormulaCard: FC<Props> = ({
  serviceProvider,
  serviceOffer,
  link,
}) => {
  const { formatMessage } = useI18n();
  const { isDesktop } = useMediaQuery();
  const [showPriceEstimateModal, setShowPriceEstimateModal] = useState(false);
  const { serviceFeesFeature, isLoading: isServiceFeesLoading } =
    useServiceFeesFeature();
  const {
    isEnabled: isChooseFormulaPageRedesignEnabled,
    isLoading: isChooseFormulaPageRedesignLoading,
  } = useFeatureFlag("choose_formula_page_redesign");

  if (isServiceFeesLoading || isChooseFormulaPageRedesignLoading) {
    return <LoadingScreen />;
  }

  if (isChooseFormulaPageRedesignEnabled) {
    return (
      <ChooseFormulaPageRedesignServiceFeeFormulaCard
        serviceOffer={serviceOffer}
        link={link}
      />
    );
  }

  const togglePriceEstimateModal = () =>
    setShowPriceEstimateModal(!showPriceEstimateModal);

  const handleServiceFeeCTAClick = () => {
    togglePriceEstimateModal();
    const subscriptionCard = document.getElementById(
      "subscription-formula-card"
    );
    const toggleAdvantages = document.getElementById(
      "subscription-advantages-link"
    );
    if (toggleAdvantages) toggleAdvantages.click();

    setTimeout(() => {
      if (subscriptionCard) subscriptionCard.scrollIntoView();
    }, 300);
  };

  const serviceProviderName = serviceProvider.user.firstName;

  const serviceFeePercentage =
    serviceFeesFeature?.details?.chargedToClient?.clientServiceFee?.percentage;

  return (
    <Card
      data-testid="service-fee-formula-card"
      header={
        <Heading as="h3" paddingBottom={[2, 4]}>
          <ResponsiveFormattedMessage
            mobile={messages.serviceFeeFormula}
            desktop={messages.serviceFeeFormulaShort}
          />
        </Heading>
      }
      footer={
        <RouterLink
          as={Button}
          colorScheme="seaGreen"
          size="lg"
          width="full"
          data-testid="choose-service-fee-formula"
          {...link}
        >
          <FormattedMessage {...messages.choose} />
        </RouterLink>
      }
    >
      <Box marginBottom="6">
        <Text marginBottom="4">
          <FormattedMessage
            {...messages.xServiceFeeOnService}
            values={{
              serviceProviderName,
              serviceFeePercentage,
            }}
          />
          {!!isDesktop && (
            <InfoTooltip
              text={formatMessage(messages.serviceFeeRingTwiceCosts)}
              mt={"2.5px"}
              ml={0.5}
            />
          )}
        </Text>
        {!isDesktop ? (
          <>
            <Button
              variant="link"
              color="gray.700"
              onClick={togglePriceEstimateModal}
              paddingX="0"
            >
              <Icon as={EuroCircle} marginRight="2" />
              <FormattedMessage {...messages.viewPriceEstimate} />
            </Button>
            <Drawer
              placement="bottom"
              onClose={togglePriceEstimateModal}
              isOpen={showPriceEstimateModal}
            >
              <ServiceOfferPreview
                serviceOfferId={serviceOffer?.id}
                handleServiceFeeCTAClick={handleServiceFeeCTAClick}
                showServiceFeeCTA
                showServiceFees={false}
                pt="0"
                px="2"
              />
            </Drawer>
          </>
        ) : (
          <>
            <ServiceOfferPreview
              serviceOfferId={serviceOffer?.id}
              colorScheme="sand"
              p="5"
            />
          </>
        )}
      </Box>
    </Card>
  );
};
